import { useCallback, type FunctionComponent, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { getTestId } from '@trello/test-ids';
import type { GAModalTestIds } from '@trello/test-ids';

import { showGAAnnouncementSpotlight } from '../betaGASharedState';
import GAModalImagePngSrc from './beta-ga-modal-image.png';

import * as styles from './GAAnnouncementModalEnterprise.module.less';

export const GAAnnouncementModalEnterprise: FunctionComponent<{
  onClose: () => void;
}> = ({ onClose }) => {
  const intl = useIntl();

  const onClickClose = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
    showGAAnnouncementSpotlight();
  }, [onClose]);

  const onClickDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissGAAnnouncementModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
    showGAAnnouncementSpotlight();
  }, [onClose]);

  const onClickReadMore = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'readMoreGAAnnouncementModalEnterpriseButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
  }, [onClose]);

  return (
    <div className={styles.gaAnnouncementModal}>
      <img className={styles.headingImage} src={GAModalImagePngSrc} alt="" />
      <DynamicButton
        className={styles.closeButton}
        iconBefore={<CloseIcon size="medium" />}
        onClick={onClickClose}
        aria-label={intl.formatMessage({
          id: 'templates.dialog_close_button.close-dialog-label',
          defaultMessage: 'Close dialog',
          description: 'Aria label for the close dialog button',
        })}
        data-testid={getTestId<GAModalTestIds>('ga-modal-close-button')}
      />
      <div className={styles.contentContainer}>
        <FormattedMessage
          id="templates.beta_ga.a-new-trello-is-coming"
          defaultMessage="A new Trello is coming to everyone soon"
          description="GA modal content"
          tagName="h1"
        />
        <FormattedMessage
          id="templates.enterprise_pp.coming-soon.sub-title"
          description="Announce enterprise GA PP"
          defaultMessage="We're gradually releasing the new Trello to everyone."
          tagName="p"
        />
        &nbsp;
        <FormattedMessage
          id="templates.beta_ga.with-new-features-like-inbox-and-planner"
          defaultMessage="With new features like <strong>Inbox</strong> and <strong>Planner</strong>, you can capture to-dos and schedule time to get things done."
          description="GA modal content"
          tagName="span"
          values={{
            strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
          }}
        />
        <div className={styles.ctasContainer}>
          <Button appearance="subtle" onClick={onClickDismiss}>
            <FormattedMessage
              id="templates.beta_ga.dismiss-modal-button"
              defaultMessage="Dismiss"
              description="GA modal dismiss button"
            />
          </Button>
          <Button
            appearance="primary"
            onClick={onClickReadMore}
            href="https://trello.com/tour"
            linkTarget="_blank"
          >
            <FormattedMessage
              id="templates.enterprise_pp.actions.read-more"
              description="CTA"
              defaultMessage="Read more"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
